<script lang="ts">
type Props = {
  hasCenteredItems?: boolean;
  hasCenteredSlides?: boolean;
  hasMultipleSlidesPerView?: boolean;
  hasZoom?: boolean;
  isSidebar?: boolean;
  isRotatingCarousel?: boolean;
  shouldLoop?: boolean;
  spaceBetween?: number;
};
</script>

<script setup lang="ts">
import { ref, onMounted, computed, Fragment, watch, nextTick } from 'vue';
import { useSlots } from 'vue';
import { register } from 'swiper/element/bundle';
import 'swiper/css';
import 'swiper/css/pagination';
import { isDesktop, isWidescreen, isTablet, isMobile } from '../../utils/breakpoints';
import Icon from '../Icon/Icon.vue';

const props = withDefaults(defineProps<Props>(), {
  hasCenteredItems: false,
  hasCenteredSlides: false,
  hasMultipleSlidesPerView: false,
  hasZoom: false,
  isRotatingCarousel: false,
  isSidebar: false,
  spaceBetween: 0,
  shouldLoop: true,
});

register();

const swiper = ref();
const slots = useSlots();

const isPrevDisabled = ref(false);
const isNextDisabled = ref(false);

const defaultSlot = computed(() => slots.default?.() ?? []);

// ? Destructure the fragments in single components
const normalizedChildren = computed(() => {
  return defaultSlot.value.flatMap((vnode) => {
    if (vnode.type === Fragment && Array.isArray(vnode.children)) {
      return vnode.children;
    }
    return [vnode];
  });
});

const updateButtonStates = () => {
  if (props.isRotatingCarousel && swiper.value?.swiper) {
    const swiperInstance = swiper.value.swiper;
    isPrevDisabled.value = swiperInstance.isBeginning;
    isNextDisabled.value = swiperInstance.isEnd;
  }
};

const goPrev = () => {
  if (!isPrevDisabled.value) {
    swiper.value?.swiper?.slidePrev();
  }
};

const goNext = () => {
  if (!isNextDisabled.value) {
    swiper.value?.swiper?.slideNext();
  }
};

const slidesPerViewRef = ref(5);

const updateSlidesPerView = () => {
  if (!props.hasMultipleSlidesPerView) {
    slidesPerViewRef.value = 1;
  } else if (isDesktop.value || isWidescreen.value) {
    slidesPerViewRef.value = 4;
  } else if (isTablet.value) {
    slidesPerViewRef.value = props.hasCenteredSlides ? 2 : 3;
  } else if (isMobile.value) {
    slidesPerViewRef.value = 1.45;
  } else {
    slidesPerViewRef.value = 1;
  }
};

const shouldLoopRef = computed(
  () =>
    props.shouldLoop &&
    normalizedChildren.value.length > 1 &&
    (normalizedChildren.value.length > slidesPerViewRef.value || props.isSidebar),
);

const shouldShowNavButtons = computed(
  () =>
    (isDesktop.value || isWidescreen.value) &&
    (shouldLoopRef.value || (normalizedChildren.value.length > 1 && props.isRotatingCarousel)),
);

watch([isDesktop, isWidescreen, isTablet, isMobile], () => {
  updateSlidesPerView();
});

onMounted(() => {
  updateSlidesPerView();
  const swiperEl = swiper.value;

  // ? Inject custom styles into Shadow DOM
  Object.assign(swiperEl, {
    injectStyles: [
      `
        .swiper {
          position: relative;
      ${props.hasZoom ? `.swiper-wrapper { margin-block: 2.3rem; }` : ''}

        .swiper-pagination {
          position: relative;
          left: 50%;
          transform: translateX(-50%);
          background-color: #ffffff;
          height: 1.6rem;
          width: fit-content;
          justify-content: center;
          align-items: center;
          border: 0.1rem solid #e9e9e9;
          border-radius: 10rem;
          box-shadow: 0 0.6rem 0.8rem 0.2rem rgba(70, 70, 70, 0.12);
          margin-bottom: 0.6rem
        }

        .swiper-pagination:not(.swiper-pagination-lock) {
          display: flex;
        }

        .swiper-pagination-bullet {
          background-color: #9b9b9b;
          border: 0.1rem solid #9b9b9b;
          opacity:1
        }

        .swiper-pagination-bullet:hover {
         background-color: #00cfff;
         border-color: #00cfff;
       }

        .swiper-pagination-bullet:active,
        .swiper-pagination-bullet:focus,
        .swiper-pagination-bullet:focus-visible {
          background-color: #ffffff;
          border-color: #00cfff;
          outline: 0.1rem solid #00cfff;
        }

        .swiper-pagination-bullet-active {
          width: 2.4rem;
          background-color: #00cfff;
          border-color: #00cfff;
          border-radius: 0.8rem;
      }


        .swiper-pagination-bullet-active:active,
        .swiper-pagination-bullet-active:focus,
        .swiper-pagination-bullet-active:focus-visible {
         background-color: #00cfff;
         outline:none;
      }}`,
    ],
  });

  swiperEl.initialize();

  // Add event listeners for button state updates
  if (props.isRotatingCarousel) {
    nextTick(() => {
      const swiperInstance = swiperEl.swiper;
      if (swiperInstance) {
        // Update button states on slide change
        swiperInstance.on('slideChange', updateButtonStates);
        swiperInstance.on('reachBeginning', updateButtonStates);
        swiperInstance.on('reachEnd', updateButtonStates);

        // Initial button state update
        updateButtonStates();
      }
    });
  }
});
</script>

<template>
  <div :class="$style.container">
    <swiper-container
      ref="swiper"
      :autoplay-delay="isRotatingCarousel ? 100000000 : '5000'"
      :autoplay-disable-on-interaction="!isRotatingCarousel"
      :centered-slides="hasCenteredSlides"
      init="false"
      :loop="shouldLoopRef"
      :pagination="{ clickable: true, hideOnClick: false }"
      :round-lengths="true"
      :slide-to-clicked-slide="true"
      :slides-per-view="slidesPerViewRef"
      :space-between="spaceBetween"
    >
      <swiper-slide v-for="(vnode, index) in normalizedChildren" :key="index">
        <component
          :is="vnode"
          :class="[$style.slideContainer, { [$style.centeredItems]: hasCenteredItems }]"
        />
      </swiper-slide>
    </swiper-container>

    <button
      v-if="shouldShowNavButtons"
      aria-label="previous slide"
      :class="$style.previousButton"
      :disabled="isPrevDisabled"
      @click="goPrev"
    >
      <Icon name="chevron_left" />
    </button>
    <button
      v-if="shouldShowNavButtons"
      aria-label="next slide"
      :class="$style.nextButton"
      :disabled="isNextDisabled"
      @click="goNext"
    >
      <Icon name="chevron_right" />
    </button>
  </div>
</template>

<style module lang="scss" src="./Slider1XP.scss" />
